import * as React from "react";
import FooterBottom, { IFooterBottomProps } from "./footer-bottom/footer-bottom";
import FooterContact, { IFooterContactProps } from "./footer-contact/footer-contact";
import FooterList, { IFooterListProps } from "./footer-list/footer-list";
import FooterLogo, { IFooterLogoProps } from "./footer-logo/footer-logo";
import Accordion from "../accordion/accordion";
import AccordionItem from "../accordion/accordion-item/accordion-item";
import AccordionHeader from "../accordion/accordion-header/accordion-header";
import AccordionContent from "../accordion/accordion-content/accordion-content";
import { useAccordion } from "../accordion/useAccordion";
import FooterText, { IFooterTextProps } from "~/components/footer/footer-text/footer-text";
import classNames from "classnames";
import { assertNever } from "~/utils/type-utils";

export enum FooterType {
    CONTACT = "Contact",
    LINK_LIST = "LinkList",
    TEXT_AREA = "TextArea"
}

export interface IFooterContact {
    type: FooterType;
    title: string;
    phone: IFooterContactProps["phone"];
    waitLabel?: IFooterContactProps["waitLabel"];
    waitValue?: IFooterContactProps["waitValue"];
    waitSeverity?: IFooterContactProps["waitSeverity"];
    listItems: IFooterContactProps["listItems"];
}

export interface IFooterLinkList {
    type: FooterType;
    title: string;
    links: IFooterListProps["links"];
    socialLinks?: IFooterListProps["socialLinks"];
}

export interface IFooterText {
    type: FooterType;
    title?: string;
    logoUrl?: IFooterTextProps["logoUrl"];
    logoSrc?: IFooterTextProps["logoSrc"];
    logoAlt?: IFooterTextProps["logoAlt"];
    logoTitle?: IFooterTextProps["logoTitle"];
    richText?: IFooterTextProps["richText"];
}

type FooterContent = IFooterContact | IFooterLinkList | IFooterText;

export interface IFooterProps {
    footerLogoUrl?: IFooterLogoProps["footerLogoUrl"];
    footerLogoImgSrc?: IFooterLogoProps["footerLogoImgSrc"];
    footerLogoImgAlt?: IFooterLogoProps["footerLogoImgAlt"];
    footerLogoTitle?: IFooterLogoProps["footerLogoTitle"];
    footerContent: Array<FooterContent>;
    footerBottomCopyrightLabel: IFooterBottomProps["footerBottomCopyrightLabel"];
    footerBottomLinks: IFooterBottomProps["footerBottomLinks"];
    isSignupFlow?: boolean;
}

const Footer: React.FC<IFooterProps> = props => {
    const [activeIndex, setActiveIndex] = useAccordion();

    const renderFooterItem = (item: FooterContent) => {
        switch (item.type) {
            case FooterType.CONTACT:
                return <FooterContact {...(item as IFooterContact)} />;
            case FooterType.LINK_LIST:
                return <FooterList {...(item as IFooterLinkList)} />;
            case FooterType.TEXT_AREA:
                return <FooterText {...(item as IFooterText)} />;
            default:
                return assertNever(item); // Throws error here if there are missing cases
        }
    };

    const { footerLogoUrl, footerLogoImgSrc, footerLogoImgAlt, footerLogoTitle, isSignupFlow } = props;
    const rootClasses = classNames("footer", { "footer--signup": isSignupFlow });

    return (
        <div className={rootClasses}>
            <div className="footer-top">
                {footerLogoTitle && footerLogoUrl && footerLogoImgSrc && footerLogoImgAlt && (
                    <FooterLogo
                        footerLogoUrl={footerLogoUrl}
                        footerLogoImgSrc={footerLogoImgSrc}
                        footerLogoImgAlt={footerLogoImgAlt}
                        footerLogoTitle={footerLogoTitle}
                    />
                )}
                <Accordion className="footer__accordion">
                    {props.footerContent.map((item, index) => {
                        const isActive = activeIndex === index;
                        return (
                            <AccordionItem
                                key={index}
                                isActive={isActive}
                                renderHeader={
                                    <AccordionHeader
                                        isActive={isActive}
                                        onClick={() => setActiveIndex(index)}
                                    >
                                        <h4 className="footer__accordion-title">{item.title}</h4>
                                    </AccordionHeader>
                                }
                                renderContent={<AccordionContent>{renderFooterItem(item)}</AccordionContent>}
                            />
                        );
                    })}
                </Accordion>
            </div>
            <FooterBottom {...props} />
        </div>
    );
};

export default Footer;
